import React from 'react'

const PIECE_GLYPHS: Record<string, string> = {
  K: '♔',
  Q: '♕',
  R: '♖',
  B: '♗',
  N: '♘',
  P: '♙',
  k: '♚',
  q: '♛',
  r: '♜',
  b: '♝',
  n: '♞',
  p: '♟'
}

const BOARD_SIZE = 8

const FILES = 'abcdefgh'

const expandRank = (rank: string): string[] =>
  [...rank].flatMap(symbol => (/\d/.test(symbol) ? Array(Number(symbol)).fill('') : [symbol]))

export const parsePlacement = (fen: string): string[][] | null => {
  const ranks = fen.trim().split(/\s+/)[0]?.split('/') ?? []
  const squares = ranks.map(expandRank)
  const isValid =
    squares.length === BOARD_SIZE &&
    squares.every(rank => rank.length === BOARD_SIZE && rank.every(s => !s || PIECE_GLYPHS[s]))
  return isValid ? squares : null
}

const squareName = (rowIndex: number, columnIndex: number, flipped: boolean): string =>
  flipped
    ? `${FILES[BOARD_SIZE - 1 - columnIndex]}${rowIndex + 1}`
    : `${FILES[columnIndex]}${BOARD_SIZE - rowIndex}`

interface FenBoardProps {
  fen: string
  flipped?: boolean
  size?: number
  lastMove?: string | null
  showCoordinates?: boolean
}

const FenBoard = ({
  fen,
  flipped = false,
  size = 160,
  lastMove = null,
  showCoordinates = false
}: FenBoardProps) => {
  const squares = parsePlacement(fen)
  if (!squares) {
    return (
      <div className="pz-board pz-board-invalid" style={{ width: size, height: size }}>
        Invalid FEN
      </div>
    )
  }

  const rows = flipped ? [...squares].reverse().map(rank => [...rank].reverse()) : squares
  const highlighted = lastMove ? [lastMove.slice(0, 2), lastMove.slice(2, 4)] : []

  return (
    <div
      className="pz-board"
      style={{ width: size, height: size, fontSize: size / BOARD_SIZE - 2 }}
      aria-label={fen}
    >
      {rows.flatMap((rank, rowIndex) =>
        rank.map((piece, columnIndex) => {
          const square = squareName(rowIndex, columnIndex, flipped)
          const shade = (rowIndex + columnIndex) % 2 ? 'dark' : 'light'
          const isHighlighted = highlighted.includes(square)
          return (
            <span
              key={square}
              data-square={square}
              className={`pz-square ${shade} ${isHighlighted ? 'last-move' : ''}`}
            >
              {piece ? PIECE_GLYPHS[piece] : ''}
              {showCoordinates && columnIndex === 0 && (
                <span className="pz-coord pz-coord-rank">{square[1]}</span>
              )}
              {showCoordinates && rowIndex === BOARD_SIZE - 1 && (
                <span className="pz-coord pz-coord-file">{square[0]}</span>
              )}
            </span>
          )
        })
      )}
    </div>
  )
}

export default FenBoard
